
<template>
  <div>
    <GridPagination
      v-if="pagination"
      :currentPage="currentPage"
      :pageSize="pagination.pageSize"
      :currentTotal="data.length"
      :totalItems="pagination.totalItems"
      :onPageChange="setPage"
    />
    <div class="table-responsive" :style="pagination ? 'margin-top: 1em;' : ''">
      <table class="table table-hover">
        <thead>
          <tr>
            <th
              :key="'column-header-' + column.key"
              v-for="column in settings.columns"
              @click="sort(column)"
              class="pointer"
              :class="getHeaderClasses(column)"
            >
              {{ column.title }}
              <i
                :class="{
                  'fa fa-chevron-up': currentSort == column.key,
                  asc: currentSortDirection == 'asc',
                  desc: currentSortDirection == 'desc',
                }"
              ></i>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            :key="'row-' + row[settings.trackRowBy]"
            v-for="row in sortedData"
          >
            <slot
              :row="row"
              v-for="column in settings.columns"
              :name="column.key"
            >
                <td @click="$emit('row-event', row)"
                    :class="getColumnClasses(column)"
                    :key="
                  'row-' +
                  row[settings.trackRowBy] +
                  '-column-' +
                  column.key +
                  ''
                ">
                    <slot :row="row" :name="`col-${column.key}`">
                        <template v-if="column.linkData">
                            <div class="btn btn-link btn-link-data">{{ row[column.key] }}</div>
                        </template>
                        <template v-else-if="column.useSlaDate">
                            <SLADateFormat :value="row[column.key]"
                                           :valueText="formatDate(row[column.key], column.filterDate)"></SLADateFormat>
                        </template>
                        <template v-else-if="column.filterDate">
                            {{ $formatDate(row[column.key], column.filterDate) }}
                        </template>
                        <template v-else-if="column.filterCurrency">
                            {{ $dashesIfNull($toCurrency(row[column.key])) }}
                        </template>
                        <template v-else-if="column.toFixed">
                            {{ Number.parseFloat(row[column.key]).toFixed(column.toFixed) }}
                        </template>
                        <template v-else-if="column.useDashIfNull">
                            {{ $dashesIfNull(row[column.key]) }}
                        </template>
                        <template v-else>
                            {{ row[column.key] }}
                        </template>

                        <template v-if="column.suffix">
                            {{ column.suffix }}
                        </template>
                    </slot>
                </td>
            </slot>
          </tr>
        </tbody>
        <tfoot v-if="hasFooterObjects">
          <tr>
              <th :key="'column-footer-' + column.key"
                  v-for="column in settings.columns"
                  :class="getFooterClasses(column)">
                  <template v-if="column.footer && column.footer.sum">
                      <template v-if="column.filterCurrency">
                          {{ $dashesIfNull($toCurrency(getColumnSum(column))) }}
                      </template>
                      <template v-else-if="column.toFixed">
                          {{ Number.parseFloat(getColumnSum(column)).toFixed(column.toFixed) }}
                      </template>
                      <template v-else>
                          {{ getColumnSum(column) }}
                      </template>
                      <template v-if="column.suffix">
                          {{ column.suffix }}
                      </template>
                  </template>           
              </th>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
    import GridPagination from "@/components/GridPagination";
    import SLADateFormat from "@/components/templates/SLADateFormat";
    import moment from "moment";

    export default {
        components: {
            GridPagination,
            SLADateFormat,
        },
        props: {
            // Should contain a "trackRowBy" value
            // and an object array of columns with "title"
            // and a "key"
            // it's optional to add a "noWrap" boolean value.
            settings: { type: Object, required: true },
            // An array that fills the table.
            data: { type: Array, required: true },
            // Should contain a pageSize and a total number of items.
            pagination: { type: Object, required: false },
        },
        data() {
            return {
                currentSortDirection: "asc",
                currentSort: null,
                currentPage: 1,
            };
        },
        methods: {
            sort(column) {
                if (column.isSortable === false) return;

                if (column.key === this.currentSort)
                    this.currentSortDirection =
                        this.currentSortDirection === "asc" ? "desc" : "asc";

                this.currentSort = column.key;
                this.$emit('header-sorted', {
                    key: this.currentSort,
                    direction: this.currentSortDirection,
                });
            },
            setPage(i) {
                this.currentPage = i;
            },
            getColumnClasses(column) {
                let classes = [];

                if (column.noWrap)
                    classes.push('no-wrap');

                if (this.settings.clickableRows)
                    classes.push('pointer');

                if (column.alignRight)
                    classes.push('text-right');

                if (column.align)
                    classes.push(`text-${column.align}`);

                return classes.join(' ');
            },
            getHeaderClasses(column) {
                let classes = [];

                if (this.currentSort == column.key)
                    classes.push('active');

                if (!column.isSortable)
                    classes.push('cursor-default');

                if (column.alignRight)
                    classes.push('text-right');

                if (column.align)
                    classes.push(`text-${column.align}`);

                return classes.join(' ');
            },
            getFooterClasses(column) {
                let classes = [];

                if (column.alignRight)
                    classes.push('text-right');

                if (column.align)
                    classes.push(`text-${column.align}`);

                return classes.join(' ');
            },
            getColumnSum(column) {
                return this.data.reduce(function (a, b) {
                    return a + b[column.key];
                }, 0);
            },
        },
        computed: {
            sortedData() {
                let sortedList = this.data.slice().sort((a, b) => {
                    let modifier = 1;

                    if (this.currentSortDirection == "desc") modifier = -1;

                    if (a[this.currentSort] < b[this.currentSort]) return -1 * modifier;

                    if (a[this.currentSort] > b[this.currentSort]) return 1 * modifier;

                    return 0;
                });

                if (!this.pagination) return sortedList;

                return sortedList.slice(
                    (this.currentPage - 1) * this.pagination.pageSize,
                    this.currentPage * this.pagination.pageSize
                );
            },
            hasFooterObjects() {
                return this.settings.columns.some((x) => x.footer);
            },
        },
    };
</script>

<style lang="scss" scoped>
    .table {
        thead {
            th {
                -webkit-touch-callout: none;
                -webkit-user-select: none;
                -ms-user-select: none;
                user-select: none;
            }
        }

        tr {
            td {
                vertical-align: middle;
            }
        }

        th {
            .fa-chevron-up {
                transition: transform 0.2s;
            }

            .fa-chevron-up.asc {
                transform: rotate(0) !important;
            }

            .fa-chevron-up.desc {
                transform: rotate(180deg) !important;
            }
        }
    }

    .no-wrap {
        white-space: nowrap;
    }

    .pointer {
        cursor: pointer !important;
    }

    .cursor-default {
        cursor: default !important;
    }

    .btn-link-data {
        padding: 0;
    }
</style>